import type { Ref } from 'vue';
import type { RouteRecordNameGeneric } from 'vue-router';
import type {
  HelpcenterSidebarNavigationItem,
  Navigations,
} from '@/stores/useNavigationsStore';
import type { Tabs } from '@/stores/useTabsStore';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { ref, toRaw, watch } from 'vue';
import { isCancel } from 'axios';

import {
  fetchGetHelpcenterArticleById,
  fetchGetHelpcenterArticleRoute,
  fetchGetHelpcenterArticleSearch,
  fetchGetHelpcenterFaq,
  fetchGetHelpcenterRecentUpdates,
  fetchGetHelpcenterTopSearches,
  fetchPostHelpcenterArticleByIdDownvote,
  fetchPostHelpcenterArticleByIdUpvote,
} from '@/helpers/fetchers';

export interface HelpcenterSideBarDataComposable {
  isFetchingFaqArticleList: Ref<boolean>;
  isFetchingTopSearchesArticleList: Ref<boolean>;
  isFetchingRecentUpdatesArticleList: Ref<boolean>;
  isFetchingSearchArticleList: Ref<boolean>;
  isFetchingRouteArticleList: Ref<boolean>;
  isFetchingArticle: Ref<boolean>;
  isFetchingUpvoteArticle: Ref<boolean>;
  isFetchingDownvoteArticle: Ref<boolean>;
  fetchedFaqArticleList: Ref<openapi.ListFAQContentOpenapi | undefined>;
  fetchedTopSearchesArticleList: Ref<
    openapi.ListTopSearchesContentOpenapi | undefined
  >;
  fetchedRecentUpdatesArticleList: Ref<
    openapi.ListRecentUpdatesContentOpenapi | undefined
  >;
  fetchedSearchArticleList: Ref<
    openapi.SearchArticlesContentOpenapi | undefined
  >;
  fetchedRouteArticleList: Ref<
    openapi.GetArticlesByRouteContentOpenapi | undefined
  >;
  fetchedArticle: Ref<openapi.GetArticleByIdContentOpenapi | undefined>;
  upvoteArticle: (id: number) => Promise<void>;
  downvoteArticle: (id: number) => Promise<void>;
}

export default function useHelpcenterSideBarData(
  helpcenterSidebar: Ref<boolean>,
  helpcenterTab: Ref<Tabs['helpcenter']>,
  helpcenterSidebarNavigation: Ref<Navigations['helpcenterSidebar']>,
  routeName: Ref<RouteRecordNameGeneric>,
): HelpcenterSideBarDataComposable {
  const isFetchingFaqArticleList = ref(false);
  const isFetchingTopSearchesArticleList = ref(false);
  const isFetchingRecentUpdatesArticleList = ref(false);
  const isFetchingSearchArticleList = ref(false);
  const isFetchingRouteArticleList = ref(false);
  const isFetchingArticle = ref(false);
  const isFetchingUpvoteArticle = ref(false);
  const isFetchingDownvoteArticle = ref(false);
  const fetchedFaqArticleList = ref<openapi.ListFAQContentOpenapi>();
  const fetchedTopSearchesArticleList =
    ref<openapi.ListTopSearchesContentOpenapi>();
  const fetchedRecentUpdatesArticleList =
    ref<openapi.ListRecentUpdatesContentOpenapi>();
  const fetchedSearchArticleList = ref<openapi.SearchArticlesContentOpenapi>();
  const fetchedRouteArticleList =
    ref<openapi.GetArticlesByRouteContentOpenapi>();
  const fetchedArticle = ref<openapi.GetArticleByIdContentOpenapi>();

  let routeArticleListController: AbortController | undefined;
  let searchArticleListController: AbortController | undefined;
  let articleController: AbortController | undefined;

  async function _fetchFaqArticleList(): Promise<void> {
    fetchedFaqArticleList.value = undefined;
    isFetchingFaqArticleList.value = true;
    await fetchGetHelpcenterFaq()
      .then((data) => {
        fetchedFaqArticleList.value = data;
      })
      .finally(() => {
        isFetchingFaqArticleList.value = false;
      });
  }

  async function _fetchTopSearchesArticleList(): Promise<void> {
    fetchedTopSearchesArticleList.value = undefined;
    isFetchingTopSearchesArticleList.value = true;
    await fetchGetHelpcenterTopSearches()
      .then((data) => {
        fetchedTopSearchesArticleList.value = data;
      })
      .finally(() => {
        isFetchingTopSearchesArticleList.value = false;
      });
  }

  async function _fetchRecentUpdatesArticleList(): Promise<void> {
    fetchedRecentUpdatesArticleList.value = undefined;
    isFetchingRecentUpdatesArticleList.value = true;
    await fetchGetHelpcenterRecentUpdates()
      .then((data) => {
        fetchedRecentUpdatesArticleList.value = data;
      })
      .finally(() => {
        isFetchingRecentUpdatesArticleList.value = false;
      });
  }

  async function _fetchRouteArticleList(name: string): Promise<void> {
    fetchedRouteArticleList.value = undefined;
    if (routeArticleListController) routeArticleListController.abort();
    isFetchingRouteArticleList.value = true;
    routeArticleListController = new AbortController();
    await fetchGetHelpcenterArticleRoute(
      { name },
      {
        signal: routeArticleListController.signal,
      },
    )
      .then((data) => {
        fetchedRouteArticleList.value = data;
      })
      .catch((err: unknown) => {
        if (!isCancel(err)) throw err;
      })
      .finally(() => {
        isFetchingRouteArticleList.value = false;
      });
  }

  async function _fetchSearchArticleList(query: string): Promise<void> {
    fetchedSearchArticleList.value = undefined;
    if (searchArticleListController) searchArticleListController.abort();
    isFetchingSearchArticleList.value = true;
    searchArticleListController = new AbortController();
    await fetchGetHelpcenterArticleSearch(
      { query },
      {
        signal: searchArticleListController.signal,
      },
    )
      .then((data) => {
        fetchedSearchArticleList.value = data;
      })
      .catch((err: unknown) => {
        if (!isCancel(err)) throw err;
      })
      .finally(() => {
        isFetchingSearchArticleList.value = false;
      });
  }

  async function _fetchArticle(id: number): Promise<void> {
    fetchedArticle.value = undefined;
    if (articleController) articleController.abort();
    isFetchingArticle.value = true;
    articleController = new AbortController();
    await fetchGetHelpcenterArticleById(
      { id },
      {
        signal: articleController.signal,
      },
    )
      .then((data) => {
        fetchedArticle.value = data;
      })
      .catch((err: unknown) => {
        if (!isCancel(err)) throw err;
      })
      .finally(() => {
        isFetchingArticle.value = false;
      });
  }

  watch(
    [
      helpcenterSidebar,
      helpcenterTab,
      () => [...helpcenterSidebarNavigation.value],
      routeName,
    ],
    async (newValue, oldValue) => {
      if (helpcenterSidebar.value && helpcenterTab.value === 'browse') {
        // TODO
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const newHelpcenterSidebarNavigation = newValue[2] || [];
        const oldHelpcenterSidebarNavigation = oldValue[2] || [];
        const newRouteName = newValue[3];
        const oldRouteName = oldValue[3];
        const newLastNav = toRaw(
          newHelpcenterSidebarNavigation[
            newHelpcenterSidebarNavigation.length - 1
          ],
        ) as HelpcenterSidebarNavigationItem | undefined;
        const oldLastNav = toRaw(
          oldHelpcenterSidebarNavigation[
            oldHelpcenterSidebarNavigation.length - 1
          ],
        ) as HelpcenterSidebarNavigationItem | undefined;
        if (!newLastNav) {
          if (
            newRouteName !== oldRouteName ||
            (!isFetchingRouteArticleList.value &&
              !fetchedRouteArticleList.value)
          ) {
            if (newRouteName && typeof newRouteName === 'string') {
              void _fetchRouteArticleList(newRouteName);
            }
          }

          if (!isFetchingFaqArticleList.value && !fetchedFaqArticleList.value) {
            void _fetchFaqArticleList();
          }

          if (
            !isFetchingTopSearchesArticleList.value &&
            !fetchedTopSearchesArticleList.value
          ) {
            void _fetchTopSearchesArticleList();
          }

          if (
            !isFetchingRecentUpdatesArticleList.value &&
            !fetchedRecentUpdatesArticleList.value
          ) {
            void _fetchRecentUpdatesArticleList();
          }
          // TODO
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        } else if (newLastNav) {
          if (newLastNav.type === 'article') {
            if (
              oldLastNav?.type === 'article' &&
              oldLastNav.id === newLastNav.id
            ) {
              return;
            }
            void _fetchArticle(newLastNav.id);
            // TODO
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          } else if (newLastNav.type === 'search') {
            if (
              oldLastNav?.type === 'search' &&
              oldLastNav.query === newLastNav.query
            ) {
              return;
            }
            void _fetchSearchArticleList(newLastNav.query);
          }
        }
      }
    },
    { immediate: true, deep: true },
  );

  async function upvoteArticle(id: number): Promise<void> {
    if (isFetchingUpvoteArticle.value) return;
    isFetchingUpvoteArticle.value = true;
    try {
      await fetchPostHelpcenterArticleByIdUpvote({ id });
    } finally {
      isFetchingUpvoteArticle.value = false;
    }
  }

  async function downvoteArticle(id: number): Promise<void> {
    if (isFetchingDownvoteArticle.value) return;
    isFetchingDownvoteArticle.value = true;
    try {
      await fetchPostHelpcenterArticleByIdDownvote({ id });
    } finally {
      isFetchingDownvoteArticle.value = false;
    }
  }

  return {
    isFetchingFaqArticleList,
    isFetchingTopSearchesArticleList,
    isFetchingRecentUpdatesArticleList,
    isFetchingSearchArticleList,
    isFetchingRouteArticleList,
    isFetchingArticle,
    isFetchingUpvoteArticle,
    isFetchingDownvoteArticle,
    fetchedFaqArticleList,
    fetchedTopSearchesArticleList,
    fetchedRecentUpdatesArticleList,
    fetchedSearchArticleList,
    fetchedRouteArticleList,
    fetchedArticle,
    upvoteArticle,
    downvoteArticle,
  };
}
